import * as React from 'react'
import { CalendarIcon, CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import { CoworkersIcon } from '~/components/icons/coworkers'
import { useRepoMembers } from '~/hooks-data/use-repo-members'
import {
  DatePickerProps,
  DatePickerWithRange,
} from '~/sotano-kit/components/date/range-picker'
import { Button } from '~/sotano-kit/primitives/button'
import {
  makeFullNameFromClerkUser,
  makeShortNameFromClerkUser,
} from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { VariantProps, cva } from 'class-variance-authority'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { PublicUserData } from '~/hooks-data/use-user-data'
import { format } from 'date-fns'
import { clsx } from 'clsx'

const $filters = cva('', {
  variants: {
    size: {
      full: 'grid pb-4 mt-2 gap-y-2',
      icons: 'flex gap-x-1 items-center h-[1.3125rem]',
    },
  },
  defaultVariants: {
    size: 'full',
  },
})

type FiltersProps = {
  membersProps: Omit<MembersFilterProps, 'children'>
  rangeProps: Omit<DatePickerProps, 'children'>
  className?: string
} & VariantProps<typeof $filters>

export const Filters = ({
  membersProps,
  rangeProps,
  size,
  className,
}: FiltersProps) => {
  return (
    <div className={$filters({ size, className })}>
      <MembersFilter
        {...membersProps}
        align={size === 'icons' ? 'start' : 'end'}
      >
        {size === 'icons' ? (
          <IconButton
            size="petite"
            className={clsx(
              membersProps.selection.length > 0 &&
                'bg-dark-control-base bg-opacity-15 text-dark-control-base'
            )}
          >
            <CoworkersIcon height={15} width={15} />
          </IconButton>
        ) : (
          (repoMembers) => (
            <Button
              id="date"
              intent="select"
              radius="md"
              align="left"
              icon={
                membersProps.selection[0] ? (
                  <BaseHubAvatar
                    size="xsmall"
                    id={membersProps.selection[0]}
                    name=""
                  />
                ) : (
                  <CoworkersIcon height={12} width={12} />
                )
              }
            >
              <span className="truncate grow">
                {!membersProps.selection.length
                  ? 'All users'
                  : membersProps.selection
                      .map((id) => {
                        const match = repoMembers?.find(
                          (member) => member.id === id
                        )
                        if (!match) return ''
                        return makeShortNameFromClerkUser(match)
                      })
                      .join(', ')}
              </span>

              <CaretSortIcon height={14} width={14} className="shrink-0" />
            </Button>
          )
        )}
      </MembersFilter>
      <DatePickerWithRange
        {...rangeProps}
        align={size === 'icons' ? 'start' : 'end'}
      >
        {size === 'icons' ? (
          <IconButton
            size="petite"
            className={clsx(
              rangeProps.selection &&
                'bg-dark-control-base bg-opacity-15 text-dark-control-base'
            )}
          >
            <CalendarIcon height={15} width={15} />
          </IconButton>
        ) : (
          <Button
            id="date"
            intent="select"
            radius="md"
            align="left"
            icon={<CalendarIcon height={12} width={12} />}
          >
            <span className="grow truncate">
              {rangeProps.selection?.from ? (
                rangeProps.selection.to ? (
                  <>
                    {format(rangeProps.selection.from, 'LLL dd, y')} -{' '}
                    {format(rangeProps.selection.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(rangeProps.selection.from, 'LLL dd, y')
                )
              ) : (
                'All time'
              )}
            </span>
            <CaretSortIcon height={14} width={14} className="shrink-0" />
          </Button>
        )}
      </DatePickerWithRange>
    </div>
  )
}

type MembersFilterProps = {
  selection: string[]
  handleSelection: (previousValue: React.SetStateAction<string[]>) => void
  children:
    | React.ReactNode
    | ((repoMembers: PublicUserData[] | null) => React.ReactNode)
  align?: 'start' | 'end'
}

const MembersFilter = ({
  selection,
  handleSelection,
  children,
  align,
}: MembersFilterProps) => {
  const { data: repoMembers } = useRepoMembers()

  const handleMemberSelect = React.useCallback(
    (id: string) => {
      handleSelection((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      )
    },
    [handleSelection]
  )

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        {typeof children === 'function' ? children(repoMembers) : children}
      </PopoverTrigger>

      <PopoverContent className="w-[240px] p-1" align={align}>
        <Button
          unstyled
          focusStyled
          className="flex items-center w-full h-8 px-3 text-xs transition-colors rounded-md cursor-default gap-x-2 text-dark-bright hover:bg-dark-shade-hover"
          onClick={() => handleSelection([])}
        >
          <span className="p-0.5">
            <CoworkersIcon height={16} width={16} />
          </span>
          <span className="truncate">All users</span>
          {selection.length === 0 && (
            <CheckIcon height={15} width={15} className="ml-auto" />
          )}
        </Button>
        {repoMembers?.map((member) => (
          <Button
            unstyled
            focusStyled
            className="flex items-center w-full h-8 px-3 text-xs transition-colors rounded-md cursor-default gap-x-2 text-dark-bright hover:bg-dark-shade-hover"
            key={member.id}
            onClick={() => handleMemberSelect(member.id)}
          >
            <BaseHubAvatar size="petite" id={member.id} name="" />
            <span className="truncate">
              {makeFullNameFromClerkUser(member)}
            </span>
            {selection.includes(member.id) && (
              <CheckIcon height={15} width={15} className="ml-auto" />
            )}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

// import { FunnelIcon } from '~/components/icons/funnel'
// <Popover>
//   <PopoverTrigger asChild>
//     <Button
//       unstyled
//       focusStyled
//       className="flex items-center gap-1 mt-2 mb-3 font-medium leading-normal transition-colors cursor-default hover:text-dark-dim text-dark-faint"
//     >
//       <FunnelIcon height={15} width={15} />
//       <span>Filter</span>
//     </Button>
//   </PopoverTrigger>
// <PopoverContent>Filter</PopoverContent>
// </Popover>
